import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripDotsIcon, I } from '@/shared/icons';
import { cn } from '@/shared/lib/cn';
import { invoiceTaxCategoryOptions, type InvoiceItemPresetResponse } from '@/shared/api';
import { formatMoney } from '@/shared/lib/format';

interface Props {
  preset: InvoiceItemPresetResponse;
  onEdit: () => void;
  onDelete: () => void;
}

/**
 * Eine Katalogposition als sortierbare Zeile — dieselbe Anatomie wie `SortableBlockRow`.
 *
 * <p>Der Griff lag bis Sprint 55 hinter `opacity-0 group-hover:opacity-100` und die drei
 * Icon-Knöpfe maßen 26 px. Auf einem Touchgerät gab es die Reihenfolge damit gar nicht und die
 * Knöpfe traf man nur zufällig. Griff jetzt dauerhaft sichtbar, `touch-none`, damit der Zug nicht
 * als Scrollen gedeutet wird, und 44-px-Ziele nach §9.</p>
 */
export function SortablePresetRow({ preset, onEdit, onDelete }: Props) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: preset.id,
  });
  const tax = invoiceTaxCategoryOptions.find((option) => option.value === preset.taxCategory);

  return (
    <li
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
      }}
      className={cn(
        'border-line grid grid-cols-[1fr_auto] items-center gap-3 border-b p-2 last:border-b-0',
        !preset.isActive && 'opacity-55',
      )}
    >
      <div className="min-w-0">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="text-ink truncate font-serif text-base">{preset.label}</span>
          {!preset.isActive && (
            <span className="text-ink-faint text-[11px] uppercase tracking-wider">
              nicht in der Auswahl
            </span>
          )}
        </div>
        <div className="text-ink-soft mt-0.5 text-xs tabular-nums">
          {formatMoney(preset.unitPrice, preset.currency)}
          {tax ? ` · ${tax.label} (${tax.hint})` : ''}
        </div>
        {preset.notes && <div className="text-ink-faint mt-1 truncate text-xs">{preset.notes}</div>}
      </div>

      <div className="flex items-center">
        <IconButton
          label={`${preset.label} verschieben`}
          className="cursor-grab touch-none active:cursor-grabbing"
          {...attributes}
          {...listeners}
        >
          <GripDotsIcon />
        </IconButton>
        <IconButton label={`${preset.label} bearbeiten`} onClick={onEdit}>
          <I name="Edit" size={16} />
        </IconButton>
        <IconButton
          label={`${preset.label} entfernen`}
          onClick={onDelete}
          className="hover:text-sealwax"
        >
          <I name="Close" size={16} />
        </IconButton>
      </div>
    </li>
  );
}

function IconButton({
  label,
  children,
  className,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={cn(
        'text-ink-soft hover:bg-card-alt hover:text-ink focus-visible:ring-sage flex h-11 w-11 shrink-0 items-center justify-center rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2',
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}
